/**
 * DashboardQuickNav — anchor-link strip shown at the top of
 * /dashboard so the user can jump straight to Your Projects (the
 * page's primary CTA) without scrolling past the three summary
 * panels. Each link also communicates whether the target section is
 * showing live data or synthesised preview data so the user knows
 * what they're about to see.
 *
 * The nav is sticky below the app header so it stays visible as the
 * user scrolls through the big dashboard sections.
 */

import { useEffect, useState } from 'react';

type SectionKind = 'live' | 'preview' | 'primary';

interface NavSection {
  id: string;
  label: string;
  kind: SectionKind;
  count?: number | string;
}

export function DashboardQuickNav({
  projectCount,
}: {
  projectCount: number;
}) {
  const sections: NavSection[] = [
    {
      id: 'your-projects',
      label: 'Your projects',
      kind: 'primary',
      count: projectCount,
    },
    { id: 'structural-summary', label: 'Structural', kind: 'preview' },
    { id: 'operational-summary', label: 'Operational', kind: 'preview' },
    { id: 'security-summary', label: 'Security', kind: 'preview' },
  ];

  // Track which section is in view so the nav chip can be
  // highlighted. Cheap implementation using IntersectionObserver;
  // we don't depend on the exact "current" being pixel-perfect.
  const [active, setActive] = useState<string>('your-projects');
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
            return;
          }
        }
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 },
    );
    for (const s of sections) {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toneMap: Record<SectionKind, string> = {
    primary:
      'bg-primary text-primary-foreground border-primary hover:bg-primary/90',
    live:
      'border-green-300 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20',
    preview:
      'border-amber-300 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20',
  };

  const kindChip = (kind: SectionKind) => {
    if (kind === 'primary') return null;
    const map: Record<'live' | 'preview', string> = {
      live: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      preview: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    };
    return (
      <span className={`text-[9px] px-1 rounded uppercase tracking-wide ${map[kind]}`}>
        {kind}
      </span>
    );
  };

  return (
    <nav
      data-testid="dashboard-quick-nav"
      aria-label="Dashboard sections"
      className="sticky top-0 z-20 -mx-4 px-4 py-2 bg-background/95 backdrop-blur border-b border-border mb-4"
    >
      <div className="flex items-center gap-2 overflow-x-auto text-sm">
        <span className="text-xs text-muted-foreground shrink-0 mr-1">
          Jump to:
        </span>
        {sections.map(s => {
          const isActive = active === s.id;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`quick-nav-${s.id}`}
              onClick={e => {
                e.preventDefault();
                const target = document.getElementById(s.id);
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  target.focus({ preventScroll: true });
                }
              }}
              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${toneMap[s.kind]} ${isActive ? 'ring-2 ring-offset-1 ring-offset-background' : ''}`}
            >
              <span className="font-medium">{s.label}</span>
              {s.count !== undefined && (
                <span className="text-[11px] font-mono opacity-80">
                  {s.count}
                </span>
              )}
              {kindChip(s.kind)}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
